import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiBell, FiCheck } from 'react-icons/fi';
import notificationService from '../../services/notification.service';

const TYPE_ICONS = {
  order_update: '📦',
  review: '⭐',
  question: '💬',
  system: '🔔',
  promotion: '🎉',
  low_stock: '⚠️',
  points: '🏆',
};

const NotificationBell = () => {
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { data } = await notificationService.getUnreadCount();
        setUnread(data.data.count);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000); // every 30s
    return () => clearInterval(interval);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleDropdown = async () => {
    if (!open) {
      setLoading(true);
      try {
        const { data } = await notificationService.getAll({ limit: 8 });
        setNotifications(data.data);
      } catch {}
      setLoading(false);
    }
    setOpen(!open);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setUnread(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch {}
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationService.markRead(id);
      setUnread((prev) => Math.max(0, prev - 1));
      setNotifications((prev) => prev.map((n) => n._id === id ? { ...n, isRead: true } : n));
    } catch {}
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <li className="nav-item me-2 position-relative" ref={dropdownRef}>
      <button
        className="nav-link btn btn-link p-0 border-0 position-relative"
        onClick={toggleDropdown}
        title="Notifications"
      >
        <FiBell size={18} />
        {unread > 0 && (
          <span
            className="position-absolute badge bg-danger rounded-pill"
            style={{ top: '-6px', right: '-8px', fontSize: '0.6rem', padding: '2px 5px' }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="position-absolute dropdown-menu show"
          style={{
            right: 0, top: '100%', marginTop: '8px',
            width: 'min(340px, calc(100vw - 32px))', maxHeight: '420px', overflowY: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: '1px solid var(--fd-border, #E5E7EB)',
          }}
        >
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
            <strong className="small">Notifications</strong>
            {unread > 0 && (
              <button className="btn btn-sm text-primary p-0 border-0" onClick={handleMarkAllRead}>
                <FiCheck size={14} className="me-1" />Mark all read
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-3 text-muted small">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <FiBell size={24} className="mb-2 d-block mx-auto opacity-50" />
              <small>No notifications</small>
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n._id}
                className={`px-3 py-2 border-bottom d-flex gap-2 ${!n.isRead ? 'bg-primary bg-opacity-10' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => { if (!n.isRead) handleMarkRead(n._id); }}
              >
                <span style={{ fontSize: '1.2rem' }}>{TYPE_ICONS[n.type] || '🔔'}</span>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="d-flex justify-content-between">
                    <strong className="small text-truncate">{n.title}</strong>
                    <small className="text-muted flex-shrink-0 ms-2">{timeAgo(n.createdAt)}</small>
                  </div>
                  <p className="mb-0 small text-muted text-truncate">{n.message}</p>
                  {n.link && (
                    <Link to={n.link} className="small text-primary" onClick={() => setOpen(false)}>
                      View →
                    </Link>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </li>
  );
};

export default NotificationBell;
